import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  CheckCircle, Lock, Eye, EyeOff, AlertTriangle, Loader2, KeyRound, ArrowRight
} from "lucide-react";

const ActivarCuenta = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [tokenInfo, setTokenInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [password, setPassword] = useState("");
  const [confirmar, setConfirmar] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [doneEmail, setDoneEmail] = useState("");

  useEffect(() => {
    axios.get(`${API}/auth/verificar-token/${token}`).then(res => {
      setTokenInfo(res.data);
      setLoading(false);
    }).catch(err => {
      setError(err.response?.data?.detail || "Enlace invalido o expirado");
      setLoading(false);
    });
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmar) {
      toast.error("Las contrasenas no coinciden");
      return;
    }
    if (password.length < 6) {
      toast.error("Minimo 6 caracteres");
      return;
    }
    setSubmitting(true);
    try {
      const endpoint = tokenInfo?.type === "reset" ? "/auth/reset-password" : "/auth/activar-cuenta";
      const res = await axios.post(`${API}${endpoint}`, { token, password });
      setDoneEmail(res.data.email || "");
      setDone(true);
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al procesar");
    } finally {
      setSubmitting(false);
    }
  };

  const isReset = tokenInfo?.type === "reset";

  return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center">
          <Link to="/" className="inline-block">
            <h1 className="font-display text-3xl tracking-tight text-emerald-900">
              Celebra<span className="font-bold">Mid</span>
            </h1>
          </Link>
        </div>

        <Card className="border-stone-200 shadow-sm">
          <CardContent className="p-6">
            {loading ? (
              <div className="text-center py-8">
                <Loader2 className="w-8 h-8 mx-auto text-emerald-700 animate-spin" />
                <p className="text-sm text-stone-500 mt-3">Verificando enlace...</p>
              </div>
            ) : error ? (
              <div className="text-center py-8 space-y-4">
                <AlertTriangle className="w-12 h-12 mx-auto text-red-400" />
                <div>
                  <p className="font-medium text-stone-900">Enlace no valido</p>
                  <p className="text-sm text-stone-500 mt-1">{error}</p>
                </div>
                <div className="flex flex-col gap-2">
                  <Link to="/login">
                    <Button variant="outline" className="w-full border-stone-300">Ir a iniciar sesion</Button>
                  </Link>
                </div>
              </div>
            ) : done ? (
              <div className="text-center py-8 space-y-4" data-testid="cuenta-activada-success">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
                  <CheckCircle className="w-8 h-8 text-emerald-600" />
                </div>
                <div>
                  <h2 className="font-display text-xl text-stone-900">
                    {isReset ? "Contrasena actualizada" : "Cuenta activada"}
                  </h2>
                  <p className="text-sm text-stone-500 mt-2">
                    {isReset
                      ? "Tu contrasena ha sido actualizada correctamente."
                      : "Tu cuenta ha sido activada. Ya puedes iniciar sesion."}
                  </p>
                  {doneEmail && (
                    <p className="text-sm text-stone-700 mt-1">
                      Email: <strong>{doneEmail}</strong>
                    </p>
                  )}
                </div>
                <Button onClick={() => navigate("/login")} className="w-full btn-primary" data-testid="btn-ir-login">
                  Iniciar sesion <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="text-center">
                  <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <KeyRound className="w-6 h-6 text-emerald-700" />
                  </div>
                  <h2 className="font-display text-xl text-stone-900" data-testid="activar-title">
                    {isReset ? "Nueva contrasena" : "Crea tu contrasena"}
                  </h2>
                  {tokenInfo?.name && (
                    <p className="text-sm text-stone-600 mt-1">
                      Hola, <strong>{tokenInfo.name}</strong>
                    </p>
                  )}
                  <p className="text-sm text-stone-500 mt-1">
                    {isReset
                      ? "Define tu nueva contrasena para acceder al sistema."
                      : "Define tu contrasena para activar tu cuenta en CelebraMid."}
                  </p>
                  {tokenInfo?.email && (
                    <p className="text-xs text-stone-400 mt-1">{tokenInfo.email}</p>
                  )}
                </div>

                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <Label>Nueva contrasena</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                      <Input
                        type={showPw ? "text" : "password"}
                        placeholder="Minimo 6 caracteres"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        minLength={6}
                        className="pl-10 pr-10 border-stone-300"
                        data-testid="input-new-password"
                      />
                      <button type="button" onClick={() => setShowPw(!showPw)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400">
                        {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Confirmar contrasena</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                      <Input
                        type="password"
                        placeholder="Repite tu contrasena"
                        value={confirmar}
                        onChange={(e) => setConfirmar(e.target.value)}
                        required
                        minLength={6}
                        className="pl-10 border-stone-300"
                        data-testid="input-confirm-password"
                      />
                    </div>
                  </div>
                  {password && confirmar && password !== confirmar && (
                    <p className="text-xs text-red-500">Las contrasenas no coinciden</p>
                  )}
                </div>

                <Button type="submit" disabled={submitting || !password || !confirmar}
                  className="w-full btn-primary" data-testid="btn-activar-cuenta">
                  {submitting ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle className="w-4 h-4 mr-2" />
                  )}
                  {isReset ? "Actualizar contrasena" : "Activar mi cuenta"}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ActivarCuenta;
